import 'keen-slider/keen-slider.min.css';
import {
  ArrowDownIcon,
  Box,
  IconButton,
  Typography
} from '@customink/pigment-react';
import {styled} from '@mui/material/styles';
import useResizeObserver from '@react-hook/resize-observer';
import KeenSlider from 'keen-slider';
import React, {useCallback, useEffect, useRef, useState} from 'react';
import useBreakpoint from 'hooks/useBreakpoint';
import promoImage from 'images/september-promo-trending-banner/september-promo-trending-banner.png';
import promoImageTablet from 'images/september-promo-trending-banner/september-promo-trending-banner-tablet.png';
import RelatedProductCard from 'product_details_page/RelatedProducts/RelatedProductCard';
import {
  EVENT_TYPE,
  fireSeptemberPromoEvent
} from 'utils/metrics/septemberPromoBanner';
import config from '../../config';
import {BannerSkeleton} from './components/LoadingSkeleton';
import useTrendingProducts from './hooks/useTrendingProducts';

const BannerContainer = styled(Box)(() => ({
  '@media (max-width: 1023px)': {
    borderRadius: 0,
    flexDirection: 'column',
    height: 'auto',
    marginLeft: '-1rem',
    marginRight: '-1rem',
    width: 'calc(100% + 2rem)'
  },
  '@media (min-width: 1024px) and (max-width: 1299px)': {
    height: '27.50625rem'
  },
  backgroundColor: '#f1f4fe',
  borderRadius: '0.5rem',
  display: 'flex',
  height: '30.5625rem',
  marginBottom: '2.5rem',
  marginTop: '2.5rem',
  overflow: 'hidden',
  position: 'relative',
  width: '100%'
}));

const BannerImage = styled(Box)(({isMobile, isTablet}) => ({
  '@media (max-width: 1023px)': {
    height: 'auto',
    width: '100%'
  },
  '@media (min-width: 1024px) and (max-width: 1299px)': {
    width: '29.98125rem'
  },
  ...(!isMobile &&
    !isTablet && {
      backgroundImage: `url(${config.assets.imagePath}${promoImage})`,
      backgroundPosition: 'center center',
      backgroundRepeat: 'no-repeat',
      backgroundSize: 'cover'
    }),
  flexShrink: 0,
  height: '100%',
  position: 'relative',
  width: '33.3125rem'
}));

const BannerImageText = styled(Box)(() => ({
  '@media (max-width: 699px)': {
    textAlign: 'center',
    width: 'calc(100% - 4rem)'
  },
  backgroundColor: 'rgba(255, 255, 255, 0.8)',
  borderRadius: '0.5rem',
  display: 'flex',
  flexDirection: 'column',
  left: '50%',
  padding: '2rem',
  position: 'absolute',
  top: '50%',
  transform: 'translate(-50%, -50%)',
  width: 'calc(100% - 7rem)'
}));

const BannerContent = styled(Box)(() => ({
  '@media (max-width: 1023px)': {
    padding: '1.5rem 1rem',
    width: '100%'
  },
  '@media (min-width: 1024px) and (max-width: 1299px)': {
    width: 'calc(100% - 29.98125rem)'
  },
  display: 'flex',
  flex: 1,
  flexDirection: 'column',
  padding: '1.875rem 0 1.875rem 1.5rem',
  width: 'calc(100% - 33.3125rem)'
}));

const BannerContentHeader = styled(Box)(() => ({
  alignItems: 'center',
  display: 'flex',
  justifyContent: 'space-between',
  padding: '1.5rem 0'
}));

const BannerContentButtons = styled(Box)(() => ({
  display: 'flex',
  gap: '0.5rem',
  marginRight: '1.5rem'
}));

const BannerContentBody = styled('div')(() => ({
  '.keen-slider': {
    '@media (max-width: 1023px)': {
      height: '18.9375rem',
      overflow: 'visible !important'
    },
    display: 'flex !important',
    height: '100%',
    overflow: 'hidden !important',
    position: 'relative',
    touchAction: 'pan-y',
    userSelect: 'none',
    WebkitTouchCallout: 'none'
  },
  '.keen-slider__slide': {
    '@media (max-width: 1023px)': {
      height: '18.9375rem',
      minHeight: '18.9375rem'
    },
    minHeight: '100%',
    overflow: 'hidden',
    position: 'relative'
  },
  '@media (max-width: 1023px)': {
    overflow: 'visible'
  },
  width: '100%'
}));

const SeptemberPromoTrendingBanner = () => {
  const breakpoint = useBreakpoint();
  const isMobile = breakpoint === 'sm';
  const isTablet = breakpoint === 'md';
  const isSmallDesktop = breakpoint === 'lg';
  const {isLoading, products} = useTrendingProducts();

  const [isFirstSlide, setIsFirstSlide] = useState(true);
  const [isLastSlide, setIsLastSlide] = useState(false);

  const getSlidesPerView = () => {
    if (isMobile) return 1.6;
    if (isSmallDesktop) return 3;
    return 3.5;
  };

  const getGap = () => {
    if (isMobile) return 8;
    if (isTablet) return 16;
    return 24;
  };

  const slidesPerView = getSlidesPerView();
  const gap = getGap();

  const sliderRef = useRef(null);
  const instanceRef = useRef(null);

  useEffect(() => {
    if (sliderRef.current && products.length > 0) {
      instanceRef.current = new KeenSlider(sliderRef.current, {
        created: () => {
          setIsFirstSlide(false);
          setIsLastSlide(false);
        },
        initial: 0,
        loop: true,
        mode: 'free-snap',
        slideChanged: () => {
          setIsFirstSlide(false);
          setIsLastSlide(false);
        },
        slidesPerView,
        spacing: gap
      });
    }

    return () => {
      if (instanceRef.current) {
        instanceRef.current.destroy();
        instanceRef.current = null;
      }
    };
  }, [products.length, slidesPerView, gap, isMobile]);

  useEffect(() => {
    if (products.length > 0) {
      setIsFirstSlide(false);
      setIsLastSlide(false);

      fireSeptemberPromoEvent({
        eventType: EVENT_TYPE.PROMO_VIEW
      });

      fireSeptemberPromoEvent({
        eventType: EVENT_TYPE.MODULE_VIEW
      });
    }
  }, [products.length, slidesPerView, isMobile]);

  const boundingBoxRef = useRef(null);
  useResizeObserver(boundingBoxRef, () => instanceRef?.current?.refresh());

  const handleLeftClick = useCallback(
    (e) => {
      e.stopPropagation();
      if (instanceRef?.current) {
        instanceRef.current.prev();
        fireSeptemberPromoEvent({
          eventType: EVENT_TYPE.CAROUSEL_PREV
        });
      }
    },
    [fireSeptemberPromoEvent]
  );

  const handleRightClick = useCallback(
    (e) => {
      e.stopPropagation();
      if (instanceRef?.current) {
        instanceRef.current.next();
        fireSeptemberPromoEvent({
          eventType: EVENT_TYPE.CAROUSEL_NEXT
        });
      }
    },
    [fireSeptemberPromoEvent]
  );

  if (isLoading) {
    return <BannerSkeleton />;
  }

  const productCards = products.map((product) => (
    <RelatedProductCard
      key={`trending-product-${product.style_id}`}
      sx={{
        width: '100%',
        ...((isMobile || isTablet) && {
          '& .MuiCardActionArea-root': {
            display: 'flex',
            flexDirection: 'column',
            height: '100%'
          },
          '& .MuiCardActionArea-root > div:first-of-type': {
            flexShrink: 0,
            height: '9rem !important',
            paddingTop: '0 !important',
            width: '100% !important'
          },
          '& .MuiCardContent-root': {
            display: 'flex',
            flex: '1 1 auto',
            flexDirection: 'column',
            gap: '0.25rem !important',
            minHeight: 'calc(100% - 9rem) !important',
            padding: '0.5rem !important'
          },
          '& [data-testid="reco-product-name"]': {
            marginBottom: '0 !important',
            minHeight: '2rem',
            WebkitLineClamp: 2
          },
          height: '18.9375rem'
        })
      }}
      onClick={() => {
        fireSeptemberPromoEvent({
          eventType: EVENT_TYPE.ITEM_CLICKED,
          styleId: product.style_id
        });
        window.location.href = product.link;
      }}
      imageSrc={product.image_src}
      imageSrcSet={product.image_srcset || ''}
      minQty={product.min_qty}
      name={product.name}
      ratingCount={product.rating_count}
      ratingScore={product.rating_score}
      sizing={product.sizing}
      sizes={product.sizes}
      generalComments={product.general_comments || ''}
      defaultUnitPrices={product.default_unit_prices}
      primaryCategoryId={product.primary_category_id}
      styleType={product.style_type}
      defaultQuoteQty={product.default_quote_qty}
      productId={product.style_id}
      showDiscount
    />
  ));

  if (products.length === 0) {
    return null;
  }

  return (
    <BannerContainer>
      <BannerImage isMobile={isMobile} isTablet={isTablet}>
        {isMobile || isTablet ? (
          <img
            src={`${config.assets.imagePath}${isTablet ? promoImageTablet : promoImage}`}
            alt="September Hardgoods Promo"
            style={{
              display: 'block',
              height: 'auto',
              width: '100%'
            }}
          />
        ) : null}
        <BannerImageText>
          <Typography
            sx={{
              color: 'rgba(0, 0, 0, 0.86)',
              fontFamily: 'Sharp Sans, sans-serif',
              fontSize: '0.875rem',
              fontWeight: 500,
              lineHeight: 1.5,
              marginBottom: '0.5rem'
            }}>
            The Perfect Size. Every Time.
          </Typography>
          <Typography
            sx={{
              '@media (max-width: 699px)': {
                fontSize: '1.125rem'
              },
              color: 'rgba(0, 0, 0, 0.86)',
              fontFamily: 'Sharp Sans, sans-serif',
              fontSize: '1.5rem',
              fontWeight: 700,
              lineHeight: 1.25
            }}>
            15% Off Custom Tumblers, Totes, Tech & More
          </Typography>
        </BannerImageText>
      </BannerImage>
      <BannerContent>
        <BannerContentHeader>
          <Typography
            sx={{
              color: 'rgba(0, 0, 0, 0.86)',
              fontFamily: 'Sharp Sans, sans-serif',
              fontSize: '1.5rem',
              fontWeight: 700,
              lineHeight: 1.25
            }}>
            Shop Trending Products
          </Typography>
          {!isMobile && (
            <BannerContentButtons>
              <IconButton
                onClick={handleLeftClick}
                disabled={isFirstSlide}
                disableRipple
                sx={{
                  '&:disabled': {
                    backgroundColor: 'rgba(255, 255, 255, 0.3)',
                    boxShadow: 'none',
                    opacity: 0.5
                  },
                  '&:hover': {
                    backgroundColor: isFirstSlide
                      ? 'rgba(255, 255, 255, 0.6)'
                      : 'rgba(255, 255, 255, 0.8)'
                  },
                  backgroundColor: 'rgba(255, 255, 255, 0.6)',
                  borderRadius: '50%',
                  boxShadow: isFirstSlide
                    ? 'none'
                    : '0 0.25rem 0.5rem 0 rgba(0,0,0,0.08)',
                  height: '2rem',
                  minWidth: '2rem',
                  width: '2rem'
                }}
                aria-label="scroll left">
                <ArrowDownIcon
                  sx={{
                    fill: isFirstSlide
                      ? 'rgba(0, 0, 0, 0.3)'
                      : 'rgba(0, 0, 0, 0.86)',
                    fontSize: '1.5rem',
                    transform: 'rotate(90deg)'
                  }}
                />
              </IconButton>
              <IconButton
                onClick={handleRightClick}
                disabled={isLastSlide}
                disableRipple
                sx={{
                  '&:disabled': {
                    backgroundColor: 'rgba(255, 255, 255, 0.3)',
                    boxShadow: 'none',
                    opacity: 0.5
                  },
                  '&:hover': {
                    backgroundColor: isLastSlide
                      ? 'rgba(255, 255, 255, 0.6)'
                      : 'rgba(255, 255, 255, 0.8)'
                  },
                  backgroundColor: 'rgba(255, 255, 255, 0.6)',
                  borderRadius: '50%',
                  boxShadow: isLastSlide
                    ? 'none'
                    : '0 0.25rem 0.5rem 0 rgba(0,0,0,0.08)',
                  height: '2rem',
                  minWidth: '2rem',
                  width: '2rem'
                }}
                aria-label="scroll right">
                <ArrowDownIcon
                  sx={{
                    fill: isLastSlide
                      ? 'rgba(0, 0, 0, 0.3)'
                      : 'rgba(0, 0, 0, 0.86)',
                    fontSize: '1.5rem',
                    transform: 'rotate(-90deg)'
                  }}
                />
              </IconButton>
            </BannerContentButtons>
          )}
        </BannerContentHeader>
        <BannerContentBody ref={boundingBoxRef}>
          <div
            ref={sliderRef}
            className="keen-slider"
            style={{paddingRight: '0'}}>
            {productCards.map((slide, i) => (
              <div key={slide.key} className={`keen-slider__slide slide-${i}`}>
                {slide}
              </div>
            ))}
          </div>
        </BannerContentBody>
      </BannerContent>
    </BannerContainer>
  );
};

SeptemberPromoTrendingBanner.propTypes = {};

export default SeptemberPromoTrendingBanner;
